import type {
  EsgDashboardByBranchResponse,
  EsgDashboardMonthlyResponse,
} from '../types/esg-dashboard.type';
import { safeNumber } from './reportFormatters';

/** Sum of platform-recorded incoming waste amounts when monthly breakdown rows exist. */
export function deriveIncomingWasteTotalKg(
  monthly: EsgDashboardMonthlyResponse | null
): number | null {
  if (!monthly?.incoming_waste_breakdown?.length) return null;
  let sum = 0;
  for (const row of monthly.incoming_waste_breakdown) {
    const a = safeNumber(row.amount);
    if (a === null) return null;
    sum += a;
  }
  return sum;
}

export function deriveTotalUtilityCostSar(
  monthly: EsgDashboardMonthlyResponse | null
): number | null {
  if (!monthly) return null;
  const activityLines = monthly.scope_emissions?.activity_lines ?? [];
  const parts = activityLines.length > 0
    ? activityLines.map((line) => line.amount_sar)
    : [
        monthly.water_amount_sar,
        monthly.electricity_amount_sar,
        monthly.oil_amount_sar,
        monthly.gas_amount_sar,
      ];
  let sum = 0;
  for (const p of parts) {
    const n = safeNumber(p);
    if (n === null) return null;
    sum += n;
  }
  return sum;
}

export type GriCoverageRow = {
  topic: string;
  evidence: string;
  gap: string;
};

export function buildGri306CoverageRows(
  monthly: EsgDashboardMonthlyResponse | null,
  byBranch: EsgDashboardByBranchResponse | null
): GriCoverageRow[] {
  const incoming = deriveIncomingWasteTotalKg(monthly);
  const hasBreakdown =
    !!monthly?.recycled_waste_breakdown_by_type &&
    monthly.recycled_waste_breakdown_by_type.length > 0;
  const hasBranches = !!byBranch?.branches?.length;

  return [
    {
      topic: 'Waste generation (mass flows)',
      evidence:
        incoming !== null
          ? 'Incoming waste breakdown totals are present for the reporting month.'
          : 'No incoming waste breakdown total derived for the reporting month.',
      gap:
        incoming === null
          ? 'Cannot quantify total generation from breakdown alone without complete incoming series.'
          : 'Downstream destinations beyond platform-recorded flows may be incomplete.',
    },
    {
      topic: 'Waste diverted from disposal',
      evidence: monthly
        ? `Organization diverted mass ${
            safeNumber(monthly.total_waste_diverted_kg) !== null ? 'and diversion rate appear' : 'may be incomplete'
          } in monthly figures.`
        : 'Monthly figures were not available for this export.',
      gap: 'Diversion follows platform definitions — not asserted as regulatory diversion.',
    },
    {
      topic: 'Circularity / recycling',
      evidence: hasBreakdown
        ? 'Recycled waste breakdown by type and material is available.'
        : 'Recycled breakdown detail is empty or not provided.',
      gap: 'Recycling rate vs disposal requires disposal mass — not tracked here.',
    },
    {
      topic: 'Spatial / site disaggregation',
      evidence: hasBranches
        ? 'Branch-level monthly rows are available.'
        : 'Branch-level rows are missing.',
      gap: 'Unassigned waste may exist — review unassigned row when present.',
    },
  ];
}
